import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { EmptyState } from '@bynder/design-system';
import { IconCreateGroup, IconVideoTemplate } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import { getCategoriesState, getPendingCategories } from 'packages/store/categories/categories.selectors';
import {
    getPendingTemplates,
    getTemplates,
    selectedTemplateSelector,
} from 'packages/store/templates/templates.selectors';
import { CollectionSubType, CollectionType } from 'packages/store/collections/types';
import { fetchCategories, initCategories } from 'packages/store/categories/categories.actions';
import useQueryParams from 'packages/hooks/useQueryParams';
import filtersStorage from 'packages/hooks/filtersStorage';
import useAccessRights from 'packages/hooks/useAccessRights';
import useCreativeThumbnailsUpdates from 'packages/hooks/useCreativeThumbnailsUpdates';
import { Template } from 'packages/store/templates/types';
import { INITIALIZE_TEMPLATES } from 'packages/store/types';
import {
    duplicateTemplates,
    fetchTemplates,
    getTemplate,
    initializeTemplates,
    selectTemplate,
} from 'packages/store/templates/templates.actions';
import features from 'packages/configs/features';
import roles from 'packages/configs/roles';
import Filters from 'packages/pages/components/filters';
import useFluidWidth from 'packages/pages/components/grid/useFluidWidth';
import { OwnershipFilterType } from 'packages/pages/components/filters/components/Ownership/types';
import { MediaFilterType } from 'packages/pages/components/filters/components/MediaType/types';
import { OrderFilterType, SortingFilterType } from 'packages/pages/components/filters/components/Ordering/types';
import { OverviewCount } from 'packages/pages/components/Categories.styled';
import useFilters from 'packages/pages/components/categories/useFilters';
import ListView from 'packages/pages/components/list';
import ListViewToggle from 'packages/pages/components/list/toggle';
import renderHeaders from 'packages/pages/components/list/headers';
import {
    columnWidths as templateColumnWidths,
    headers as templateHeaders,
    renderItems as renderTemplateItems,
} from 'packages/pages/categoryDetails/components/CategoryDetailsListItem';
import {
    columnWidths as categoryColumnWidths,
    headers as categoryHeaders,
    renderItems as renderCategoryItems,
} from 'packages/pages/categories/components/CategoryListItem';
import { removeEmptyProps, TEMPLATES_PAGE_SIZE, CATEGORIES_PAGE_SIZE, useScrollImproved } from 'packages/pages/utils';
import EmptyFilteredState, { isFilteredList } from 'packages/pages/components/empty/EmptyFilteredState';
import useTemplateRenameModal from 'packages/pages/components/templates/useTemplateRenameModal';
import useTemplateMoveModal from 'packages/pages/components/templates/useTemplateMoveModal';
import useTemplateDeleteModal from 'packages/pages/components/templates/useTemplateDeleteModal';
import { deselectBlankType, setPage } from 'packages/store/newDesign/newDesign.actions';
import { deselectGallery, initializeGallery } from 'packages/store/gallery/gallery.actions';
import { HiddenActions } from 'packages/pages/components/templates/TemplateContextMenu/types';
import TemplateGrid from 'packages/pages/components/templates/TemplateGrid';
import useTemplateShareModal from 'packages/pages/shareModal/useTemplateShareModal';
import { DesignSubPages } from '../../components/Sidebar/types';
import { ModalPageBody } from '../../components/DesignCreateModal.styled';
import { CategoriesTab, isCategoriesTabValue } from 'packages/pages/categories/components/CategoriesTabs/types';
import CategoriesGrid from 'packages/pages/components/categories/CategoriesGrid';
import { customerIdSelector } from 'packages/store/user/user.selectors';
import { Category } from 'packages/store/categories/types';

const HIDDEN_MENU_ACTIONS: HiddenActions = ['removeFromCategory', 'setMoveModalTemplate'];

const Categories = () => {
    const { translate } = useTranslate();
    const dispatch = useThunkDispatch();
    const location = window.location;
    const { gridContainer } = useFluidWidth();
    const {
        categories,
        isFetching: isCategoriesFetching,
        fetchOptions: categoriesFetchOptions,
        totalItems: categoriesTotalItems,
    } = useSelector(getCategoriesState);
    const pendingThumbnailIds = useSelector(getPendingCategories);
    const pendingTemplates = useSelector(getPendingTemplates);
    const {
        templates,
        loadingData: isTemplatesFetching,
        fetchOptions: templatesFetchOptions,
        totalItems: templatesTotalItems,
    } = useSelector(getTemplates);
    const customerId = useSelector(customerIdSelector);

    const { isPlatformAllowed, isAccountRoleAssigned } = useAccessRights();
    const isManageRole = isAccountRoleAssigned(roles.video_brand_studio.video.manage);

    const type: CollectionType = 'CREATIVE';
    const subType: CollectionSubType = 'TEMPLATE';

    const {
        getQueryParam,
        getAllQueryParams,
        getAllQueryParamsRaw,
        restoreQueryParams,
        setQueryParam,
        resetQueryParams,
    } = useQueryParams();

    const activeTab: CategoriesTab = isCategoriesTabValue(getQueryParam('tab'))
        ? (getQueryParam('tab') as CategoriesTab)
        : CategoriesTab.CATEGORIES;
    const isTemplatesTab = activeTab === CategoriesTab.TEMPLATES;

    const { onSearch, onSort, onOwnershipSelect, onMediaTypeSelect } = useFilters(activeTab);
    const [listViewToggle, toggleListView] = useState(false);
    const { getFiltersFromStorage, setFiltersToStorage } = filtersStorage();
    const { setShareModalTemplate, renderModalTemplateShare } = useTemplateShareModal({});

    const { setRenameModalTemplate, renderModalTemplateRename } = useTemplateRenameModal();
    const { setMoveModalTemplate, renderModalTemplateMove } = useTemplateMoveModal({ isOverview: true });
    const { setDeleteModalTemplate, renderModalTemplateDelete } = useTemplateDeleteModal();
    const selectedTemplate = useSelector(selectedTemplateSelector);

    const isTemplateSelected = (template: Template) => {
        // @ts-ignore
        return selectedTemplate?.id === template.id;
    };

    useCreativeThumbnailsUpdates(pendingThumbnailIds);
    useCreativeThumbnailsUpdates(pendingTemplates);

    // first load
    useLayoutEffect(() => {
        dispatch(initializeTemplates());
        dispatch(initializeGallery());
        dispatch(deselectBlankType());

        const storedQueries = getFiltersFromStorage('newDesign:categories');

        // removing template id from stored query params to avoid pre-selection of template
        const storedFilteredQueries =
            storedQueries?.length &&
            storedQueries
                .split('&')
                .reduce((acc, query) => {
                    if (!query.includes('template')) {
                        acc.push(query);
                    }

                    return acc;
                }, [])
                .join('&');

        if (storedFilteredQueries) {
            restoreQueryParams(storedFilteredQueries);
        }

        const querySearch = getQueryParam('search');
        const queriedSorting = getQueryParam('sortedBy');
        const queriedOrder = getQueryParam('sortOrder');
        const queriedOwnership = getQueryParam('ownership');
        const queriedType = getQueryParam('typeTemplate')?.toUpperCase();

        const queriedParams = removeEmptyProps({
            search: querySearch,
            sort: queriedSorting && queriedOrder && [queriedSorting, queriedOrder],
            ownershipSource: queriedOwnership?.toUpperCase(),
        });

        dispatch(
            initCategories({
                type,
                subType,
                ownershipSource: OwnershipFilterType.OWNED_BY_ANYONE,
                includeEmptyCollection: !isManageRole,
                ...queriedParams,
            }),
        );

        if (location.pathname === '/categories' || location.search.includes('page=categories')) {
            dispatch(
                fetchTemplates({
                    newOptions: {
                        ...templatesFetchOptions,
                        ...queriedParams,
                        creativeType: queriedType === MediaFilterType.ANY ? undefined : queriedType,
                        ownershipSource: queriedParams.ownershipSource || OwnershipFilterType.OWNED_BY_ANYONE,
                        collectionId: undefined,
                        // Templates section shows ALL templates, whether they are in a category or not.
                        // The service ignores `assignedToCollection` unless this opt-out flag is set.
                        includeAssignedToCollection: true,
                    },
                    setSelected: false,
                    fetchFromStart: true,
                }),
            );
        }

        return () => {
            if (
                location.pathname.includes('categories') ||
                (location.search.includes('page=category_details') && location.search.includes('create=true'))
            ) {
                dispatch({ type: INITIALIZE_TEMPLATES });
            }
        };
    }, []);

    useEffect(() => {
        const queriedTemplateId = getQueryParam('template');

        if (queriedTemplateId) {
            dispatch(getTemplate(queriedTemplateId));
        }
    }, []);

    // Fetches the given tab's data with the currently persisted query params
    // (search / sort / ownership / type). Filters are shared across tabs but
    // applied to whichever tab is active (see useFilters), so switching tabs
    // must re-apply them to the tab we switch to, keeping the list in sync with
    // the filter controls.
    const fetchForTab = (tab: CategoriesTab) => {
        const querySearch = getQueryParam('search');
        const queriedSorting = getQueryParam('sortedBy');
        const queriedOrder = getQueryParam('sortOrder');
        const queriedOwnership = getQueryParam('ownership')?.toUpperCase();
        const queriedType = getQueryParam('typeTemplate')?.toUpperCase();
        const sort = queriedSorting && queriedOrder ? [queriedSorting, queriedOrder] : undefined;

        if (tab === CategoriesTab.CATEGORIES) {
            dispatch(
                initCategories({
                    type,
                    subType,
                    ownershipSource: OwnershipFilterType.OWNED_BY_ANYONE,
                    includeEmptyCollection: !isManageRole,
                    ...removeEmptyProps({ search: querySearch, sort, ownershipSource: queriedOwnership }),
                }),
            );

            return;
        }

        dispatch(
            fetchTemplates({
                newOptions: {
                    search: querySearch || '',
                    creativeType: queriedType && queriedType !== MediaFilterType.ANY ? queriedType : undefined,
                    ownershipSource: queriedOwnership || OwnershipFilterType.OWNED_BY_ANYONE,
                    collectionId: undefined,
                    // Templates section shows ALL templates, whether they are in a category or not.
                    // The service ignores `assignedToCollection` unless this opt-out flag is set.
                    includeAssignedToCollection: true,
                    ...(sort ? { sort } : {}),
                },
                setSelected: false,
                fetchFromStart: true,
            }),
        );
    };

    // Re-sync the newly-active tab on tab change. Skip the initial mount, which
    // the first-load effect above already fetches.
    const didMountRef = useRef(false);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;

            return;
        }

        fetchForTab(activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (location.pathname.includes('categories') && !location.pathname.includes('categories/')) {
            setFiltersToStorage('categories', getAllQueryParams());
        }

        if (
            location.search.includes('page=categories') &&
            location.search.includes('create=true') &&
            location.search.includes('categoryId=ALL')
        ) {
            setFiltersToStorage('newDesign:categories', getAllQueryParams());
        }
    }, [getAllQueryParams()]);

    const onTemplatesScroll = useCallback(() => {
        const notLoading = !isTemplatesFetching;
        const notLastPage = templates.length < templatesTotalItems;

        if (notLoading && notLastPage) {
            const newOptions = { ...templatesFetchOptions };
            newOptions.size = TEMPLATES_PAGE_SIZE;
            newOptions.page = Math.trunc(templates.length / newOptions.size);

            dispatch(fetchTemplates({ newOptions }));
        }
    }, [dispatch, templatesFetchOptions, templatesTotalItems, templates.length, isTemplatesFetching]);

    const onCategoriesScroll = useCallback(() => {
        const notLoading = !isCategoriesFetching;
        const notLastPage = categories.length < categoriesTotalItems;

        if (notLoading && notLastPage) {
            const newOptions = { ...categoriesFetchOptions };
            newOptions.page += 1;
            newOptions.size = CATEGORIES_PAGE_SIZE;
            dispatch(fetchCategories({ ...newOptions, type }, customerId));
        }
    }, [dispatch, isCategoriesFetching, categories.length, categoriesTotalItems, customerId, categoriesFetchOptions]);

    const onTemplateSelect = (template: Template) => {
        dispatch(selectTemplate(template));
        dispatch(deselectBlankType(true));
        dispatch(deselectGallery());
    };

    const duplicateTemplate = (template: Template) => {
        dispatch(duplicateTemplates([template.id], template.categoryId));
        setQueryParam('template', '');
    };

    const onCardClick = (categoryId: number) => {
        dispatch(setPage(DesignSubPages.CATEGORY_DETAILS));
        setQueryParam('categoryId', categoryId.toString());
    };

    const menuActions = {
        setRenameModalTemplate,
        setDeleteModalTemplate,
        setMoveModalTemplate,
        setShareModalTemplate,
        showPreview: (t: Template) => {
            dispatch(selectTemplate(t));
            setQueryParam('template', t.id.toString());
            dispatch(setPage(DesignSubPages.TEMPLATE_PREVIEW));
        },
        duplicateTemplate,
    };

    const isCategoriesEmpty = !categories.length && !isCategoriesFetching;
    const isTemplatesEmpty = !templates.length && !isTemplatesFetching;
    const isFiltered = isFilteredList(getAllQueryParamsRaw());
    const isActiveListEmpty = isTemplatesTab ? isTemplatesEmpty : isCategoriesEmpty;
    const [activeSortBy, activeSortOrder] = (
        isTemplatesTab ? templatesFetchOptions.sort : categoriesFetchOptions.sort
    ) as [SortingFilterType, OrderFilterType];

    const resetTemplatesFilters = () =>
        dispatch(
            fetchTemplates({
                newOptions: { ...templatesFetchOptions, search: '', creativeType: undefined },
                setSelected: false,
                fetchFromStart: true,
            }),
        );

    const resetCategoriesFilters = () =>
        dispatch(
            initCategories({
                type,
                subType,
                search: '',
                ownershipSource: OwnershipFilterType.OWNED_BY_ANYONE,
                includeEmptyCollection: !isManageRole,
            }),
        );

    const renderTemplatesEmpty = () =>
        isFiltered ? (
            <EmptyFilteredState storageKey="categories" onResetFilters={resetTemplatesFilters} />
        ) : (
            <EmptyState
                icon={<IconVideoTemplate />}
                title={translate('pages.categories.category.empty.templates.title')}
                text={translate('pages.categories.category.empty.templates.info')}
            />
        );

    const renderCategoriesEmpty = () =>
        isFiltered ? (
            <EmptyFilteredState storageKey="categories" onResetFilters={resetCategoriesFilters} />
        ) : (
            <EmptyState
                icon={<IconCreateGroup />}
                title={translate('pages.categories.empty.title')}
                text={translate('pages.categories.empty.info')}
            />
        );

    const renderTemplatesGrid = () =>
        gridContainer.current?.clientWidth ? (
            <TemplateGrid
                templates={templates}
                menuActions={menuActions}
                hiddenMenuActions={HIDDEN_MENU_ACTIONS}
                isTemplateSelected={isTemplateSelected}
                onSelect={onTemplateSelect}
                onCardClick={onTemplateSelect}
                isLoading={isTemplatesFetching}
                totalItems={templatesTotalItems}
                loadMore={onTemplatesScroll}
                allowedIndicatorTypes={{ sharing: true, folder: true }}
            />
        ) : null;

    const renderCategoriesGrid = () =>
        gridContainer.current?.clientWidth ? (
            <CategoriesGrid
                categories={categories}
                onClick={(category: Category) => onCardClick(category.id)}
                isLoading={isCategoriesFetching}
                totalItems={categoriesTotalItems}
                loadMore={onCategoriesScroll}
                to={(category: Category) => `?create=true&page=category_details&categoryId=${category.id}`}
                allowedIndicatorTypes={{ sharing: !isManageRole }}
            />
        ) : null;

    const renderTemplatesList = () =>
        gridContainer.current?.clientWidth ? (
            <ListView
                columnWidths={templateColumnWidths}
                renderHeader={renderHeaders(templateHeaders, isTemplatesFetching, templatesFetchOptions.sort, onSort)}
                renderItem={renderTemplateItems({
                    templates,
                    sort: templatesFetchOptions.sort,
                    menuActions,
                    allowedIndicatorTypes: { sharing: !isManageRole, folder: true },
                    handleClick: onTemplateSelect,
                    hiddenMenuActions: HIDDEN_MENU_ACTIONS,
                    onSelect: onTemplateSelect,
                    isSelected: isTemplateSelected,
                })}
                isLoading={isTemplatesFetching}
                container={gridContainer}
                onScroll={onTemplatesScroll}
                scrollMode={{
                    type: 'intersection',
                    isLoading: isTemplatesFetching,
                    count: templates.length,
                    totalItems: templatesTotalItems,
                    loadMore: onTemplatesScroll,
                }}
            />
        ) : null;

    const renderCategoriesList = () =>
        gridContainer.current?.clientWidth ? (
            <ListView
                columnWidths={categoryColumnWidths}
                renderHeader={renderHeaders(
                    categoryHeaders,
                    isCategoriesFetching,
                    categoriesFetchOptions.sort as [SortingFilterType, OrderFilterType],
                    onSort,
                )}
                renderItem={renderCategoryItems({
                    categories,
                    sort: categoriesFetchOptions.sort as [SortingFilterType, OrderFilterType],
                    handleClick: (category) => onCardClick(category.id),
                    allowedIndicatorTypes: { sharing: !isManageRole },
                })}
                isLoading={isCategoriesFetching}
                container={gridContainer}
                onScroll={onCategoriesScroll}
                scrollMode={{
                    type: 'intersection',
                    isLoading: isCategoriesFetching,
                    count: categories.length,
                    totalItems: categoriesTotalItems,
                    loadMore: onCategoriesScroll,
                }}
            />
        ) : null;

    const renderTabContent = () => {
        if (isTemplatesTab) {
            if (isTemplatesEmpty) {
                return renderTemplatesEmpty();
            }

            return listViewToggle ? renderTemplatesList() : renderTemplatesGrid();
        }

        if (isCategoriesEmpty) {
            return renderCategoriesEmpty();
        }

        return listViewToggle ? renderCategoriesList() : renderCategoriesGrid();
    };

    const overviewCount = !isTemplatesTab
        ? translate('pages.categories.count.categories', { count: categoriesTotalItems })
        : translate('pages.categories.count.templates', { count: templatesTotalItems });

    useScrollImproved(isTemplatesTab ? templates : categories, gridContainer, '#categories_body');

    useEffect(() => {
        resetQueryParams(['template']);
    }, []);

    return (
        <div id="scroll-wrapper">
            <Filters data-testid="categories overview : filters">
                <Filters.FiltersLeftBlock>
                    <Filters.Search onSearch={onSearch} />
                    {(!isManageRole ||
                        (isTemplatesTab && isPlatformAllowed([features.IMAGES_ENABLED, features.VIDEOS_ENABLED]))) && (
                        <Filters.FiltersDivider />
                    )}
                    {!isManageRole && (
                        <Filters.Ownership
                            sortBy={
                                isTemplatesTab
                                    ? templatesFetchOptions.ownershipSource
                                    : categoriesFetchOptions.ownershipSource
                            }
                            onSort={onOwnershipSelect}
                        />
                    )}
                    {isTemplatesTab && isPlatformAllowed([features.IMAGES_ENABLED, features.VIDEOS_ENABLED]) && (
                        <Filters.MediaType value={templatesFetchOptions.creativeType} onSelect={onMediaTypeSelect} />
                    )}
                </Filters.FiltersLeftBlock>
                <Filters.FiltersRightBlock>
                    {!listViewToggle && (
                        <Filters.Ordering
                            sortBy={activeSortBy}
                            sortOrder={activeSortOrder}
                            onSort={onSort}
                            isDisabled={isActiveListEmpty}
                            isCreativeType={isTemplatesTab}
                        />
                    )}
                    <ListViewToggle
                        isActive={listViewToggle}
                        setListViewActive={toggleListView}
                        isDisabled={isActiveListEmpty}
                    />
                </Filters.FiltersRightBlock>
            </Filters>
            <ModalPageBody id="categories_body" ref={gridContainer} data-testid="categories overview : body">
                <OverviewCount>{overviewCount}</OverviewCount>
                {renderTabContent()}
            </ModalPageBody>
            {renderModalTemplateRename()}
            {renderModalTemplateMove()}
            {renderModalTemplateDelete()}
            {renderModalTemplateShare()}
        </div>
    );
};

export default Categories;
